import { toaster } from '@/shared/ui/toaster.component';
import { Dialog, Text } from '@noorden-insurtech/alba-design-system';
import { AlertTriangle, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { groupLifeService } from '../../../../../services';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File) => void | Promise<void>;
  /** Razão Social (or holder name) shown in the title to make context clear. */
  targetName?: string;
  /** Aviso de que a planilha substitui as vidas atuais (cotação). */
  replaceNotice?: string;
};

const EXCEL_ACCEPT =
  '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Members bulk-import modal with drag-and-drop support. Layout matches the
 * Figma reference 1:1.
 *
 * Phase 2 just captures the file and forwards it via `onConfirm`. The real
 * parsing (and batch dispatch to InsureMO `platform-pub/v1/file`) ships in
 * Phase 3. "Download Modelo" currently shows a stub alert; in Phase 3 it
 * will call `exportGroupPolicyElementByES` from InsureMO.
 */
export function ImportMembersModal({
  isOpen,
  onClose,
  onConfirm,
  targetName,
  replaceNotice,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleConfirm = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(file);
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const reset = () => {
    setFile(null);
    setIsDragging(false);
  };

  const pickFile = (selected: File | null | undefined) => {
    if (!selected) return;
    setFile(selected);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    pickFile(dropped);
  };

  const handleDownloadTemplate = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const result = await groupLifeService.downloadMembersTemplate();
      if (!result.success) {
        toaster.create({
          type: 'error',
          title: 'Baixar modelo de planilha',
          description: result.error ?? 'Não foi possível baixar o modelo.',
        });
      }
    } finally {
      setDownloading(false);
    }
  };

  const title = `Importar Vidas em ${targetName?.trim() || 'Razão Social'}`;

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleCancel}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[1080px] max-w-[95vw] rounded-[24px] bg-white p-0 shadow-xl'>
          <div className='flex flex-col gap-6 p-10'>
            {/* Header */}
            <div className='flex items-start justify-between gap-3'>
              <div className='flex items-center gap-3'>
                <Upload
                  className='h-7 w-7 text-alba-black-500'
                  strokeWidth={1.8}
                />
                <Text className='text-2xl font-bold text-alba-black-500'>
                  {title}
                </Text>
              </div>
              <button
                type='button'
                onClick={handleCancel}
                aria-label='Fechar'
                className='p-1 rounded hover:bg-alba-gray-100 transition-colors'
              >
                <X className='w-6 h-6 text-alba-black-500' strokeWidth={1.8} />
              </button>
            </div>

            {/* Description */}
            <div className='space-y-1 text-alba-gray-600'>
              <Text className='text-base'>
                Envie uma planilha em Excel (.xlsx) contendo todas as vidas que
                deseja cotar.
              </Text>
              <Text className='text-base'>
                Certifique-se de que os dados estejam completos e organizados
                conforme o modelo padrão para evitar erros no processamento.
              </Text>
            </div>

            {replaceNotice && (
              <div className='flex gap-2 rounded-lg border border-alba-warning-orange-400 bg-alba-warning-orange-400/10 p-3'>
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-alba-warning-orange-600' />
                <Text className='text-sm text-alba-gray-800'>
                  {replaceNotice}
                </Text>
              </div>
            )}

            {/* Drop zone */}
            <div
              onDragOver={e => {
                e.preventDefault();
                if (!isDragging) setIsDragging(true);
              }}
              onDragLeave={e => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={onDrop}
              className={`rounded-xl border-2 border-dashed bg-white px-6 py-16 text-center transition-colors
                ${
                  isDragging
                    ? 'border-alba-violet-500 bg-alba-violet-50'
                    : 'border-alba-gray-300'
                }`}
            >
              <div className='flex flex-col items-center gap-3'>
                <Upload
                  className='w-8 h-8 text-alba-black-500'
                  strokeWidth={1.6}
                />
                {file ? (
                  <>
                    <Text className='text-lg font-bold text-alba-black-500'>
                      {file.name}
                    </Text>
                    <Text className='text-sm text-alba-gray-500'>
                      {Math.round(file.size / 1024)} KB
                    </Text>
                    <button
                      type='button'
                      onClick={() => setFile(null)}
                      className='mt-2 text-sm font-semibold text-alba-violet-500 hover:underline'
                    >
                      Trocar arquivo
                    </button>
                  </>
                ) : (
                  <>
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Arraste e solte a planilha de vidas aqui
                    </Text>
                    <Text className='text-base text-alba-gray-500'>ou</Text>
                    <button
                      type='button'
                      onClick={() => inputRef.current?.click()}
                      className='mt-1 rounded-lg bg-[#2C2864] px-6 py-3 text-base font-semibold text-white hover:opacity-90 transition-opacity'
                    >
                      Selecione o arquivo Excel (.xlsx)
                    </button>
                  </>
                )}
              </div>
              <input
                ref={inputRef}
                type='file'
                accept={EXCEL_ACCEPT}
                onChange={e => pickFile(e.target.files?.[0])}
                className='hidden'
              />
            </div>

            {/* Footer */}
            <div className='flex items-center justify-end gap-4 pt-2'>
              <button
                type='button'
                onClick={handleCancel}
                className='cursor-pointer px-6 py-3 text-base font-bold text-alba-black-500 hover:opacity-80 transition-opacity'
              >
                Cancelar
              </button>
              <button
                type='button'
                onClick={handleDownloadTemplate}
                disabled={downloading}
                className='cursor-pointer rounded-lg bg-[#129440] px-6 py-3 text-base font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {downloading ? 'Baixando…' : 'Download Modelo'}
              </button>
              <button
                type='button'
                onClick={handleConfirm}
                disabled={!file || submitting}
                className='cursor-pointer rounded-lg bg-[#2C2864] px-6 py-3 text-base font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed'
              >
                {submitting ? 'Enviando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
